import React from 'react';
import { Briefcase, Plus, X, Pencil, Check, Trash2 } from 'lucide-react';
import type { CompanyLink } from '../../../types/Entities';
import { listCompanies, createCompany as dbCreateCompany, updateCompany as dbUpdateCompany, deleteCompany as dbDeleteCompany } from '../../../lib/companies';
import { listContactsCore } from '../../../lib/contactsCore';
import { supabase } from '../../../lib/supabase';
import { uploadCompressedImage } from '../../../lib/imageUpload';
import { addHistory } from '../../../lib/history';

interface ProfessionalSectionProps {
    contactId: string;
    userId: string;
}

export default function ProfessionalSection({ contactId, userId }: ProfessionalSectionProps) {
    const [companies, setCompanies] = React.useState<CompanyLink[]>([]);
    const [showAddCompany, setShowAddCompany] = React.useState(false);
    const [viewAllCompanies, setViewAllCompanies] = React.useState(false);
    const [newCompany, setNewCompany] = React.useState<CompanyLink>({ name: '', roleOrTitle: '', website: '', email: '', phone: '', logo: '' });
    const addLogoFileRef = React.useRef<HTMLInputElement | null>(null);
    const [isDesktop, setIsDesktop] = React.useState(false);
    React.useEffect(() => {
        const onResize = () => setIsDesktop(window.innerWidth >= 1024);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    React.useEffect(() => {
        let active = true;
        (async () => {
            if (!contactId) { setCompanies([]); return; }
            try {
                const rows = await listCompanies(contactId);
                if (!active) return;
                setCompanies(rows || []);
            } catch {
                if (active) setCompanies([]);
            }
        })();
        return () => { active = false; };
    }, [contactId]);

    const companyDomain = (url?: string) => {
        if (!url) return undefined;
        try {
            let s = url.trim();
            s = s.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
            return s.split('/')[0];
        } catch {
            return url;
        }
    };

    const [companyQuery, setCompanyQuery] = React.useState('');
    const [companyFocused, setCompanyFocused] = React.useState(false);
    const [isCompanySearching, setIsCompanySearching] = React.useState(false);
    const [companySuggestions, setCompanySuggestions] = React.useState<Array<{ name: string; website?: string | null }>>([]);

    React.useEffect(() => {
        let active = true;
        const q = companyQuery.trim();
        if (!q) { setCompanySuggestions([]); return; }
        setIsCompanySearching(true);
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: q, limit: 50, includeArchived: true } as any);
                if (!active) return;
                const mapped = (rows || [])
                    .map((r: any) => ({ name: (r.company || r.business_name || '').trim(), website: (r.business_website || r.website_url || null) }))
                    .filter((r) => r.name);
                const uniq = new Map<string, { name: string; website?: string | null }>();
                for (const m of mapped) { if (!uniq.has(m.name)) uniq.set(m.name, m); }
                setCompanySuggestions(Array.from(uniq.values()).slice(0, 6));
            } catch {
                if (active) setCompanySuggestions([]);
            } finally {
                active && setIsCompanySearching(false);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [companyQuery]);

    const addCompany = async () => {
        if (!newCompany.name?.trim() || !contactId) return;
        const detailsSnapshot = { ...newCompany };
        try {
            const saved = await dbCreateCompany(contactId, detailsSnapshot as CompanyLink);
            setCompanies(prev => [saved, ...prev]);
            setNewCompany({ name: '', roleOrTitle: '', website: '', email: '', phone: '', logo: '' } as any);
            setShowAddCompany(false);
            try { await addHistory({ contact_id: contactId, action: 'company_added', details: { name: detailsSnapshot.name, role: detailsSnapshot.roleOrTitle } }); } catch { }
        } catch { }
    };

    const removeCompany = (id: string) => { setCompanies(prev => prev.filter(c => c.id !== id)); };

    const [activeCompany, setActiveCompany] = React.useState<CompanyLink | null>(null);
    const [activeCompanyEdit, setActiveCompanyEdit] = React.useState<CompanyLink | null>(null);
    const [isCompanyEditing, setIsCompanyEditing] = React.useState(false);
    const modalLogoFileRef = React.useRef<HTMLInputElement | null>(null);

    const isCompanyDirty = React.useMemo(() => {
        if (!activeCompany || !activeCompanyEdit) return false;
        const norm = (v?: string | null) => (v ?? '').trim();
        return (
            norm(activeCompany.name) !== norm(activeCompanyEdit.name) ||
            norm(activeCompany.roleOrTitle) !== norm(activeCompanyEdit.roleOrTitle) ||
            norm(activeCompany.website) !== norm(activeCompanyEdit.website) ||
            norm(activeCompany.email) !== norm(activeCompanyEdit.email) ||
            norm(activeCompany.phone) !== norm(activeCompanyEdit.phone) ||
            norm(activeCompany.logo) !== norm(activeCompanyEdit.logo)
        );
    }, [activeCompany, activeCompanyEdit]);

    const openCompany = (c: CompanyLink) => { setActiveCompany(c); setActiveCompanyEdit({ ...c }); setIsCompanyEditing(false); };
    const closeCompany = () => { setActiveCompany(null); setActiveCompanyEdit(null); setIsCompanyEditing(false); };
    const saveCompany = async () => {
        if (!activeCompany || !activeCompanyEdit) return;
        if (!isCompanyDirty) { setIsCompanyEditing(false); return; }
        try {
            const updated = await dbUpdateCompany(activeCompany.id!, activeCompanyEdit as CompanyLink);
            setCompanies(prev => prev.map(x => x.id === activeCompany.id ? updated : x));
            closeCompany();
        } catch { }
    };
    const deleteCompany = async () => {
        if (!activeCompany) return;
        try {
            await dbDeleteCompany(activeCompany.id!);
            removeCompany(activeCompany.id!);
            closeCompany();
        } catch { }
    };

    return (
        <>
            <div className="bg-gray-900 rounded-xl">
                <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                    <div className="flex items-center space-x-2">
                        <Briefcase className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-white">Professional</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                const next = !showAddCompany; setShowAddCompany(next);
                                if (next) { setViewAllCompanies(false); setNewCompany({ name: '', roleOrTitle: '', website: companies[0]?.website || '', email: '', phone: '', logo: '' }); setCompanyQuery(''); }
                            }}
                            className="p-2 rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white"
                            aria-label={showAddCompany ? 'Close' : 'Add Company'}
                            title={showAddCompany ? 'Close' : 'Add'}
                        >
                            {showAddCompany ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                <div className="px-5 pb-5 space-y-4">
                    {showAddCompany && (
                        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                            <div className="w-full flex items-center justify-center mb-3">
                                <input ref={addLogoFileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        try { const url = await uploadCompressedImage(file, `company-logos/${contactId || 'misc'}`); setNewCompany({ ...newCompany, logo: url }); }
                                        catch { const url = URL.createObjectURL(file); setNewCompany({ ...newCompany, logo: url }); }
                                    }
                                }} />
                                <button type="button" onClick={() => addLogoFileRef.current?.click()} className="w-24 h-24 rounded-lg bg-gray-900 border border-gray-700 relative overflow-hidden group" aria-label="Upload company logo">
                                    {newCompany.logo ? (
                                        <img src={newCompany.logo} alt="logo" className="w-full h-full object-contain" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Briefcase className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                        <div className="w-8 h-8 rounded-full bg-black/50 border border-gray-600 flex items-center justify-center">
                                            <Plus className="w-4 h-4 text-white" />
                                        </div>
                                    </div>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    {(companyFocused || companyQuery) && companySuggestions.length > 0 && (
                                        <div className="mb-2">
                                            <div className="text-xs text-gray-400 mb-1">Suggestions</div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                {companySuggestions.map((s) => (
                                                    <button key={`${s.name}-${s.website || ''}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => {
                                                        setNewCompany({ ...newCompany, name: s.name, website: s.website || '', email: '', phone: '', logo: newCompany.logo || '' });
                                                        setCompanyQuery(''); setCompanyFocused(false);
                                                    }} className="flex items-center gap-2 p-2 rounded-md border border-gray-700 hover:bg-gray-700 text-left">
                                                        <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0 flex items-center justify-center border border-gray-600">
                                                            <Briefcase className="w-4 h-4 text-gray-400" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm text-gray-200 truncate">{s.name}</div>
                                                            <div className="text-xs text-gray-400 truncate">{s.website ? companyDomain(s.website) : '—'}</div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <label className="block text-xs text-gray-400 mb-1">Company Name</label>
                                    <input
                                        value={newCompany.name || companyQuery}
                                        onChange={(e) => { setCompanyQuery(e.target.value); setNewCompany({ ...newCompany, name: '' }); }}
                                        onFocus={() => setCompanyFocused(true)}
                                        onBlur={() => { setTimeout(() => setCompanyFocused(false), 120); if (!newCompany.name && companyQuery.trim()) setNewCompany({ ...newCompany, name: companyQuery.trim() }); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { const name = newCompany.name?.trim() || companyQuery.trim(); if (name) setNewCompany({ ...newCompany, name }); } }}
                                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                        placeholder="Acme Inc."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Role / Title</label>
                                    <input value={newCompany.roleOrTitle || ''} onChange={(e) => setNewCompany({ ...newCompany, roleOrTitle: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="VP of Sales" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Website</label>
                                    <input value={newCompany.website || ''} onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="https://acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Work Email</label>
                                    <input value={newCompany.email || ''} onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="name@acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Work Phone</label>
                                    <input value={newCompany.phone || ''} onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="+1 555 123 4567" />
                                </div>
                            </div>
                            <div className="flex items-center justify-end mt-4 gap-2">
                                <button onClick={() => setShowAddCompany(false)} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Cancel</button>
                                <button onClick={addCompany} disabled={!newCompany.name?.trim()} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50">Add</button>
                            </div>
                        </div>
                    )}

                    {!showAddCompany && (companies.length === 0 ? (
                        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                            <p className="text-sm text-gray-400">No companies yet. Add one to get started.</p>
                        </div>
                    ) : viewAllCompanies ? (
                        <div className="space-y-3">
                            {companies.map(c => (
                                <div key={c.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:bg-gray-700/30 cursor-pointer" onClick={() => openCompany(c)}>
                                    <div className="min-w-0 flex flex-col items-center text-center">
                                        <div className="w-24 h-24 rounded-lg bg-gray-700 overflow-hidden flex items-center justify-center border-2 border-gray-600">
                                            {c.logo ? (
                                                <img src={c.logo} alt={c.name} className="w-full h-full object-contain" />
                                            ) : (
                                                <Briefcase className="w-8 h-8 text-gray-400" />
                                            )}
                                        </div>
                                        <div className="mt-2 flex items-center gap-2">
                                            <span className="text-white font-semibold truncate">{c.name}</span>
                                        </div>
                                        <div className="text-sm text-gray-400">{c.roleOrTitle || '—'}</div>
                                        <div className="mt-2 space-y-1.5 text-sm text-left w-full">
                                            <div className="text-gray-300"><span className="text-gray-400">Work Email:</span> {c.email || '—'}</div>
                                            <div className="text-gray-300"><span className="text-gray-400">Work Phone:</span> {c.phone || '—'}</div>
                                            <div className="text-gray-300"><span className="text-gray-400">Website:</span> {c.website ? companyDomain(c.website) : '—'}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="mt-1">
                                <button onClick={() => setViewAllCompanies(false)} className="text-sm text-gray-400 hover:text-white underline">View less</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                                {(viewAllCompanies ? companies : companies.slice(0, isDesktop ? 4 : 1)).map((c) => (
                                    <div key={c.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:bg-gray-700/30 cursor-pointer" onClick={() => openCompany(c)}>
                                        <div className="min-w-0 flex flex-col items-center text-center">
                                            <div className="w-24 h-24 rounded-lg bg-gray-700 overflow-hidden flex items-center justify-center border-2 border-gray-600">
                                                {c.logo ? (
                                                    <img src={c.logo} alt={c.name} className="w-full h-full object-contain" />
                                                ) : (
                                                    <Briefcase className="w-8 h-8 text-gray-400" />
                                                )}
                                            </div>
                                            <div className="mt-2 flex items-center gap-2">
                                                <span className="text-white font-semibold truncate max-w-full">{c.name}</span>
                                            </div>
                                            <div className="text-sm text-gray-400">{c.roleOrTitle || '—'}</div>
                                            <div className="mt-2 space-y-1.5 text-sm text-left w-full">
                                                <div className="text-gray-300"><span className="text-gray-400">Work Email:</span> {c.email || '—'}</div>
                                                <div className="text-gray-300"><span className="text-gray-400">Work Phone:</span> {c.phone || '—'}</div>
                                                <div className="text-gray-300"><span className="text-gray-400">Website:</span> {c.website ? companyDomain(c.website) : '—'}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {companies.length > (isDesktop ? 4 : 1) && !viewAllCompanies && (
                                <div className="mt-1">
                                    <button onClick={() => setViewAllCompanies(true)} className="text-sm text-gray-400 hover:text-white underline">View {companies.length - (isDesktop ? 4 : 1)} more</button>
                                </div>
                            )}
                            {viewAllCompanies && companies.length > (isDesktop ? 4 : 1) && (
                                <div className="mt-1">
                                    <button onClick={() => setViewAllCompanies(false)} className="text-sm text-gray-400 hover:text-white underline">View less</button>
                                </div>
                            )}
                        </>
                    ))}
                </div>
            </div>

            {activeCompany && activeCompanyEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={closeCompany} />
                    <div className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        {/* Top-left: Delete while editing */}
                        {isCompanyEditing && (
                            <div className="absolute top-3 left-3">
                                <button onClick={deleteCompany} className="p-2 rounded-md border border-red-700 bg-red-900/30 text-red-200 hover:bg-red-900/40 inline-flex items-center gap-2" title="Delete" aria-label="Delete">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {/* Top-right: Edit / Check / Save */}
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                            {!isCompanyEditing ? (
                                <button aria-label="Edit" title="Edit" onClick={() => setIsCompanyEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                    <Pencil className="w-4 h-4" />
                                </button>
                            ) : isCompanyDirty ? (
                                <button onClick={saveCompany} className="px-2.5 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save changes" aria-label="Save changes">
                                    Save
                                </button>
                            ) : (
                                <button onClick={() => setIsCompanyEditing(false)} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Done" aria-label="Done">
                                    <Check className="w-4 h-4" />
                                </button>
                            )}
                            <button onClick={closeCompany} aria-label="Close" title="Close" className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex flex-col items-center text-center">
                            <input ref={modalLogoFileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                if (!isCompanyEditing) return;
                                const file = e.target.files?.[0];
                                if (file) {
                                    try { const url = await uploadCompressedImage(file, `company-logos/${contactId || 'misc'}`); setActiveCompanyEdit(prev => ({ ...(prev as CompanyLink), logo: url })); }
                                    catch { const url = URL.createObjectURL(file); setActiveCompanyEdit(prev => ({ ...(prev as CompanyLink), logo: url })); }
                                }
                            }} />
                            <button type="button" onClick={() => isCompanyEditing && modalLogoFileRef.current?.click()} className={`w-28 h-28 rounded-lg bg-gray-700 overflow-hidden flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-gray-600 border-2 border-gray-600`} title={activeCompanyEdit.logo ? 'Change logo' : 'Upload logo'}>
                                {activeCompanyEdit.logo ? (
                                    <img src={activeCompanyEdit.logo} alt={activeCompanyEdit.name} className="w-full h-full object-contain" />
                                ) : (
                                    <Briefcase className="w-12 h-12 text-gray-400" />
                                )}
                            </button>
                            <div className="mt-2 text-white font-semibold truncate w-full">{activeCompanyEdit.name}</div>
                        </div>

                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Company</label>
                                <input disabled={!isCompanyEditing} value={activeCompanyEdit.name || ''} onChange={(e) => setActiveCompanyEdit({ ...(activeCompanyEdit as CompanyLink), name: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Role / Title</label>
                                <input disabled={!isCompanyEditing} value={activeCompanyEdit.roleOrTitle || ''} onChange={(e) => setActiveCompanyEdit({ ...(activeCompanyEdit as CompanyLink), roleOrTitle: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Website</label>
                                <input disabled={!isCompanyEditing} value={activeCompanyEdit.website || ''} onChange={(e) => setActiveCompanyEdit({ ...(activeCompanyEdit as CompanyLink), website: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Work Email</label>
                                <input disabled={!isCompanyEditing} value={activeCompanyEdit.email || ''} onChange={(e) => setActiveCompanyEdit({ ...(activeCompanyEdit as CompanyLink), email: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Work Phone</label>
                                <input disabled={!isCompanyEditing} value={activeCompanyEdit.phone || ''} onChange={(e) => setActiveCompanyEdit({ ...(activeCompanyEdit as CompanyLink), phone: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70" />
                            </div>
                            {/* Bottom row removed; actions live in header */}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}